import "./importBulkTestOrderComponent.scss";
import * as Yup from "yup";
import React, {useCallback, useEffect, useRef, useState} from "react";
import CommonService from "../../../shared/services/common.service";
import {IAPIResponseType} from "../../../shared/models/api.model";
import {Field, FieldProps, Form, Formik, FormikHelpers, FormikProps, FormikValues} from "formik";
import FormikInputComponent from "../../../shared/components/core/formik-input/FormikInputComponent";
import FormikAutoCompleteDropdown
    from "../../../shared/components/core/formik-auto-complete-dropdown/formikAutoCompleteDropdown";
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import ButtonComponent from "../../../shared/components/core/button/ButtonComponent";
import _ from "lodash";
import Grid from "@mui/material/Grid";
import ErrorComponent from "../../../shared/components/form-controls/error/errorComponent";
import ChipComponent from "../../../shared/components/chip/ChipComponent";
import {AttachFile} from '@mui/icons-material';
import {useNavigate} from "react-router-dom";
import FeedbackComponent from "../../../shared/components/feedback/feedbackComponent";
import commonService from "../../../shared/services/common.service";

interface ImportBulkTestOrderComponentProps {
    bulkOrderDetails?: any;
    onImportDrawerClose?: Function;
    type?: string;
}

const bulkTestOrderValidationSchema = Yup.object({
    title: Yup.string().required('Title is required.').nullable(),
    facility: Yup.mixed().required('Facility is required.').nullable(),
    location: Yup.mixed().required('Location is required.').nullable(),
    Physician: Yup.mixed().required('Physician is required.').nullable(),
    test: Yup.mixed().required('Test is required.').nullable(),
    file: Yup.mixed().required('File is required').nullable(),
});

interface bulkTestOrderDetailsFormSchema {
    title: string,
    facility: any,
    location: any,
    Physician: any,
    test: any,
    file: any,
}

const bulkTestOrderDetailsFormInitialValues: bulkTestOrderDetailsFormSchema = {
    title: "",
    facility: null,
    location: null,
    Physician: null,
    test: null,
    file: null,
}


const ImportBulkTestOrderComponent = (props: ImportBulkTestOrderComponentProps) => {
    const {bulkOrderDetails, onImportDrawerClose, type} = props;
    const navigate = useNavigate();
    const [bulkTestOrderInitialValues, setBulkTestOrderInitialValues] = useState<bulkTestOrderDetailsFormSchema>(bulkTestOrderDetailsFormInitialValues);
    const [locationList, setLocationList] = useState<any>([]);
    const [isLocationListLoaded, setIsLocationListLoaded] = useState<boolean>(false);
    const [isLocationListLoading, setIsLocationListLoading] = useState<boolean>(false);
    const [isLocationListLoadingFailed, setIsLocationListLoadingFailed] = useState<boolean>(false);

    const formikRef = useRef<FormikProps<FormikValues>>(null);
    const {facilityListLite} = useSelector((state: IRootReducerState) => state.staticData);
    const excelFileRef = useRef<HTMLInputElement | null>(null);

    const [isPhysicianListLoading, setIsPhysicianListLoading] = useState<boolean>(false);
    const [physicianList, setPhysicianList] = useState<any>([]);
    const {currentUser} = useSelector((state: IRootReducerState) => state.account);
    const [testList, setTestList] = useState<any[]>([]);
    const loggedInUserFacilityDetails = currentUser?.facilityDetails
    const loggedInUserLocationDetails = currentUser?.locationDetails
    const [isTestListLoading, setIsTestListLoading] = useState<boolean>(false);
    const [isTestListLoadingFailed, setIsTestListLoadingFailed] = useState<boolean>(false);


    const getLocationList = useCallback((facilityID: any) => {
        const payload = {facilityId: facilityID, isActive: true};
        setIsLocationListLoading(true);
        setIsLocationListLoadingFailed(false);
        setIsLocationListLoaded(false);
        let locationList: any = [];
        CommonService._location.getLocationListLite(payload)
            .then((response: IAPIResponseType<any>) => {
                if (response?.data) {
                    locationList = response.data;
                }
                setLocationList(locationList);
                setIsLocationListLoading(false);
                setIsLocationListLoadingFailed(false);
                setIsLocationListLoaded(true);
            })
            .catch((error: any) => {
                setIsLocationListLoading(false);
                setIsLocationListLoaded(false);
                setIsLocationListLoadingFailed(true);
            });
    }, []);

    const getPhysicainList = useCallback((locationID: any) => {
        const payload = {};
        setIsPhysicianListLoading(true);
        CommonService._location.getPhysicianList(locationID, payload)
            .then((response: IAPIResponseType<any>) => {
                setIsPhysicianListLoading(false);
                setPhysicianList(response.data);
            })
            .catch((error: any) => {
                CommonService._alert.showToast(error?.error || `Error while Fetching physician list from the Location`, "error");
                setIsPhysicianListLoading(false);
            })
    }, []);

    const getTestList = useCallback((locationId: any) => {
        const payload = {locationId: locationId, isActive: true};
        setIsTestListLoading(true);
        setIsTestListLoadingFailed(false);
        let testList: any = [];
        CommonService._test.getTestListLite(payload)
            .then((response: IAPIResponseType<any>) => {
                if (response?.data) {
                    testList = response.data;
                }
                setTestList(testList);
                setIsTestListLoading(false);
                setIsTestListLoadingFailed(false);
            })
            .catch((error: any) => {
                setTestList(testList);
                setIsTestListLoading(false);
                setIsTestListLoadingFailed(true);
            });
    }, []);

    useEffect(() => {
        if (loggedInUserFacilityDetails) {
            setBulkTestOrderInitialValues((oldState: any) => {
                return {
                    ...oldState,
                    facility: loggedInUserFacilityDetails,
                }
            });
            if (loggedInUserLocationDetails) {
                setBulkTestOrderInitialValues((oldState: any) => {
                    return {
                        ...oldState,
                        location: loggedInUserLocationDetails,
                    }
                });
                getTestList(loggedInUserLocationDetails?.id);
            }
            getLocationList(loggedInUserFacilityDetails?.id);
        }
    }, [loggedInUserFacilityDetails, loggedInUserLocationDetails, getLocationList, getPhysicainList, getTestList]);

    const onFacilitySelection = useCallback((value: any) => {
        if (value?.id) {
            getLocationList(value?.id);
        }
    }, [getLocationList]);


    const handleExcelAttachmentUpload = useCallback((event: any) => {
        const file = event?.target?.files[0];
        if (formikRef.current) {
            formikRef.current.setFieldValue('file', file);
        }
    }, []);

    const onFormSubmit = useCallback((values: any, {
        setSubmitting,
        setErrors,
        validateForm
    }: FormikHelpers<any>) => {
        const payload = {...values};
        payload.facilityId = payload.facility?.id;
        payload.locationId = payload.location?.id;
        payload.physicianId = payload.Physician?.id;
        payload.testId = payload.test?.id;

        delete payload.facility;
        delete payload.location;
        delete payload.Physician;
        delete payload.test;

        const formData = CommonService.getFormDataFromJSON(payload);
        setSubmitting(true);
        CommonService._testOrder.bulkUploadTestOrder(formData)
            .then((response: IAPIResponseType<any>) => {
                setSubmitting(false);
                navigate(CommonService._routeConfig.BulkOrderViewRoute(response.data.id));
                if (type === 'reUpload' && onImportDrawerClose) {
                    onImportDrawerClose();
                }
            })
            .catch((error: any) => {
                CommonService.handleErrors(setErrors, error);
                setSubmitting(false);
            })
    }, [navigate, onImportDrawerClose, type]);


    useEffect(() => {
        const orderDetails: any = {};
        if (bulkOrderDetails) {
            orderDetails.title = bulkOrderDetails?.title;
            orderDetails.facility = bulkOrderDetails?.facilityDetails;
            orderDetails.location = bulkOrderDetails?.locationDetails;
            orderDetails.Physician = bulkOrderDetails?.physicianDetails;
            orderDetails.test = bulkOrderDetails?.testDetails;
            setBulkTestOrderInitialValues(orderDetails);
        }
    }, [bulkOrderDetails]);

    return (
        <div className={'import-bulk-test-order-component screen t-form-screen'}>
            <div className={'t-form t-form-drawer'}>
                <Formik
                    validationSchema={bulkTestOrderValidationSchema}
                    initialValues={bulkTestOrderInitialValues}
                    enableReinitialize={true}
                    onSubmit={onFormSubmit}
                    innerRef={formikRef}
                >
                    {({isSubmitting, values, isValid, touched, setFieldValue, validateForm, errors}) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                        }, [validateForm, values, errors]);
                        return (
                            <Form noValidate={true}>
                                <div className="t-form-controls-wrapper mrg-top-20">
                                    <FeedbackComponent className={'mrg-bottom-20'}>
                                        <div>
                                            <div>Important Requirements</div>
                                            <div className={'d-flex pdd-left-10'}>
                                                <li></li>
                                                Make sure the template you are uploading was downloaded from ‘Download
                                                Templates’ section and has exactly the same headers.
                                            </div>
                                        </div>
                                    </FeedbackComponent>
                                    <Field name={'title'}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikInputComponent
                                                    label={'Title'}
                                                    placeholder={'Enter title'}
                                                    required={true}
                                                    multiline={false}
                                                    formikField={field}
                                                />
                                            )
                                        }
                                    </Field>
                                    <Field name={'facility'}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikAutoCompleteDropdown
                                                    fullWidth={true}
                                                    label={'Facility Name'}
                                                    data={facilityListLite}
                                                    valueKey={"id"}
                                                    placeholder={'Select Facility Name'}
                                                    formikField={field}
                                                    required={true}
                                                    getOptionLabel={(option => commonService.capitalizeFirstLetterOfEachWord(option.name) || '')}
                                                    onUpdate={(value: any) => {
                                                        setFieldValue('location', '')
                                                        setFieldValue('Physician', '')
                                                        setFieldValue('test', '')
                                                        onFacilitySelection(value)
                                                    }}
                                                />
                                            )
                                        }
                                    </Field>
                                    <Field name={'location'}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikAutoCompleteDropdown
                                                    fullWidth={true}
                                                    label={'Location Name'}
                                                    data={locationList}
                                                    valueKey={"id"}
                                                    disabled={!values.facility?.id}
                                                    placeholder={'Select Location Name'}
                                                    formikField={field}
                                                    required={true}
                                                    isDataLoaded={isLocationListLoaded}
                                                    isDataLoading={isLocationListLoading}
                                                    isDataLoadingFailed={isLocationListLoadingFailed}
                                                    getOptionLabel={(option => (option.name) || '')}
                                                    onUpdate={(value: any) => {
                                                        setFieldValue('Physician', '')
                                                        setFieldValue('test', '')
                                                        if (value?.id) {
                                                            getTestList(value?.id);
                                                            getPhysicainList(value?.id);
                                                        }

                                                    }}
                                                />
                                            )
                                        }
                                    </Field>
                                    <Field name={'Physician'}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikAutoCompleteDropdown
                                                    fullWidth={true}
                                                    label={'Ordering Provider'}
                                                    data={physicianList}
                                                    valueKey={"id"}
                                                    placeholder={'Select Ordering Provider'}
                                                    disabled={!values.facility?.id}
                                                    isDataLoading={isPhysicianListLoading}
                                                    formikField={field}
                                                    getOptionLabel={(option => option ? (option?.firstName + ' ' + option.lastName) : '')}
                                                />
                                            )
                                        }
                                    </Field>

                                    <Field
                                        name={'test'}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikAutoCompleteDropdown
                                                    label={'Ordered Test'}
                                                    formikField={field}
                                                    data={testList}
                                                    valueKey={"id"}
                                                    required={true}
                                                    disabled={!values.location?.id}
                                                    isDataLoading={isTestListLoading}
                                                    isDataLoadingFailed={isTestListLoadingFailed}
                                                    placeholder={"Select Test Name"}
                                                    getOptionLabel={(option => (option.name) || '')}
                                                />
                                            )
                                        }
                                    </Field>

                                    <div className="attachment-upload-wrapper">
                                        <div className="attachment-upload-list">
                                            <div className="attachment-upload-item">
                                                <div
                                                    className="attachment-upload-item-title">
                                                    Bulk Test Order File
                                                </div>
                                                {!values.file ? <>
                                                        <input type={"file"}
                                                               className={"display-none"}
                                                               ref={excelFileRef}
                                                               accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                                               onChange={(event) => {
                                                                   handleExcelAttachmentUpload(event);
                                                               }}
                                                        />
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} sm={6} md={6}>
                                                                <ButtonComponent
                                                                    variant={"outlined"}
                                                                    fullWidth={true}
                                                                    color={'secondary'}
                                                                    handleClick={() => {
                                                                        excelFileRef.current?.click()
                                                                    }}
                                                                    type={'button'}>
                                                                    Upload
                                                                </ButtonComponent>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6} md={6}>
                                                            </Grid>
                                                        </Grid>
                                                        {
                                                            (_.get(touched, "file") && !!(_.get(errors, "file"))) &&
                                                            <ErrorComponent
                                                                errorText={(_.get(errors, "file"))}/>
                                                        }
                                                    </>
                                                    : <div className={'bulk-test-order-file'}>
                                                        <ChipComponent
                                                            label={values.file?.name}
                                                            onDelete={() => {
                                                                setFieldValue("file", null);
                                                            }}
                                                            icon={<AttachFile/>}
                                                        />
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="drawer-actions form-action-section">
                                    <ButtonComponent variant={"contained"}
                                                     fullWidth={true}
                                                     type={'submit'}
                                                     isLoading={isSubmitting}
                                                     disabled={!isValid}
                                                     color={"primary"}>
                                        Proceed
                                    </ButtonComponent>
                                </div>
                            </Form>
                        )
                    }}
                </Formik>
            </div>
        </div>
    );

};

export default ImportBulkTestOrderComponent;
